import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Wrench,
  Users,
  Laptop,
  FileText,
  ShoppingCart,
  TrendingUp,
  AlertTriangle,
  Clock,
  CheckCircle,
  Package,
  Shield,
  ShieldOff,
  ArrowRight,
  RefreshCw,
  Euro,
  Coins,
  Settings
} from "lucide-react";

const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = "primary" }) => (
  <Card className="hover-lift transition-all duration-200 hover:shadow-md">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="text-3xl font-bold mt-2">{value}</p>
          {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
          {trend && (
            <div className="flex items-center gap-1 mt-2">
              <TrendingUp className="h-4 w-4 text-green-500" />
              <span className="text-sm text-green-500 font-medium">{trend}</span>
            </div>
          )}
        </div>
        <div className={`flex h-12 w-12 items-center justify-center rounded-xl bg-${color}/10`}>
          <Icon className={`h-6 w-6 text-${color}`} style={{ color: color === "primary" ? "hsl(var(--primary))" : color }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const AlertCard = ({ title, count, type, icon: Icon }) => {
  const colors = {
    warning: "text-yellow-600 bg-yellow-50 border-yellow-200",
    danger: "text-red-600 bg-red-50 border-red-200",
    info: "text-blue-600 bg-blue-50 border-blue-200"
  };
  
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border ${colors[type]}`}>
      <Icon className="h-5 w-5" />
      <div className="flex-1">
        <p className="text-sm font-medium">{title}</p>
      </div>
      <Badge variant="secondary" className="font-mono">{count}</Badge>
    </div>
  );
};

const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState("month");

  const fetchStats = async (p) => {
    try {
      const response = await API.get(`/dashboard/stats?period=${p || period}`);
      setStats(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des statistiques");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    setRefreshing(true);
    fetchStats(newPeriod);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchStats(period);
  };

  const seedDemoData = async () => {
    try {
      await API.post("/demo/seed");
      toast.success("Données de démonstration créées");
      fetchStats();
    } catch (error) {
      toast.error("Erreur lors de la création des données démo");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Tableau de bord</h1>
          <p className="text-muted-foreground">
            Bienvenue, {user?.prenom} ! Voici l'état de votre activité.
          </p>
        </div>
        <div className="flex gap-2">
          {user?.role === "admin" && (
            <Button variant="outline" onClick={seedDemoData} data-testid="seed-demo-btn">
              <Package className="h-4 w-4 mr-2" />
              Données démo
            </Button>
          )}
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Main Stats Grid - Bento Layout */}
      <div className="grid gap-4 md:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Dossiers SAV en cours"
          value={stats?.sav?.en_cours || 0}
          subtitle={`${stats?.sav?.total || 0} au total`}
          icon={Wrench}
        />
        <StatCard
          title="Sous garantie"
          value={stats?.sav?.garantie || 0}
          icon={Shield}
          color="#10B981"
        />
        <StatCard
          title="Hors garantie"
          value={stats?.sav?.hors_garantie || 0}
          icon={ShieldOff}
          color="#F59E0B"
        />
        <StatCard
          title="Prêts à restituer"
          value={stats?.sav?.pret_a_restituer || 0}
          icon={CheckCircle}
          color="#10B981"
        />
      </div>

      {/* Revenue & Workflow Section */}
      <div className="grid gap-4 md:gap-6 grid-cols-1 lg:grid-cols-3">
        {/* Revenue Cards - Admin & Vendeur only */}
        {user?.role !== "technicien" && (
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Euro className="h-5 w-5 text-primary" />
              Chiffre d'affaires
            </CardTitle>
            <div className="flex flex-wrap gap-1 mt-2">
              {[
                { key: "week", label: "7j" },
                { key: "month", label: "Mois" },
                { key: "30days", label: "30j" },
                { key: "quarter", label: "Trim." },
                { key: "semester", label: "Sem." },
                { key: "year", label: "Année" },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => handlePeriodChange(tab.key)}
                  className={`px-2 py-1 text-xs rounded-md transition-colors ${
                    period === tab.key ? "bg-primary text-primary-foreground font-medium" : "bg-muted text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid={`period-${tab.key}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">SAV</span>
                <span className="text-2xl font-bold">{(stats?.ca_sav_mois || 0).toFixed(2)}€</span>
              </div>
              <Progress value={stats?.ca_sav_mois > 0 ? 70 : 0} className="h-2" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <ShoppingCart className="h-3.5 w-3.5" />
                  Ventes produits
                </span>
                <span className="text-2xl font-bold">{(stats?.ventes?.ca_produits || 0).toFixed(2)}€</span>
              </div>
              <Progress value={stats?.ventes?.ca_produits > 0 ? 60 : 0} className="h-2" />
              <p className="text-xs text-muted-foreground mt-1">{stats?.ventes?.nb_ventes_produits || 0} vente(s)</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <Settings className="h-3.5 w-3.5" />
                  Ventes services
                </span>
                <span className="text-2xl font-bold">{(stats?.ventes?.ca_services || 0).toFixed(2)}€</span>
              </div>
              <Progress value={stats?.ventes?.ca_services > 0 ? 40 : 0} className="h-2" />
              <p className="text-xs text-muted-foreground mt-1">{stats?.ventes?.nb_ventes_services || 0} vente(s)</p>
            </div>
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between">
                <span className="font-medium">Total</span>
                <span className="text-2xl font-bold text-primary">
                  {((stats?.ca_sav_mois || 0) + (stats?.ventes?.ca_mois || 0)).toFixed(2)}€
                </span>
              </div>
            </div>
            {(stats?.commissions_mois || 0) > 0 && (
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <Coins className="h-4 w-4" />
                    Commissions
                  </span>
                  <span className="font-mono font-medium text-green-600">
                    {(stats?.commissions_mois || 0).toFixed(2)}€
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Workflow Status */}
        <Card className={user?.role === "technicien" ? "lg:col-span-3" : "lg:col-span-2"}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              État du workflow SAV
            </CardTitle>
            <CardDescription>Répartition des dossiers par étape</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-lg bg-blue-50 border border-blue-200 text-center">
                <p className="text-3xl font-bold text-blue-600">{stats?.sav?.en_attente_diagnostic || 0}</p>
                <p className="text-sm text-blue-700 mt-1">En attente diagnostic</p>
              </div>
              <div className="p-4 rounded-lg bg-purple-50 border border-purple-200 text-center">
                <p className="text-3xl font-bold text-purple-600">{stats?.sav?.devis_en_attente || 0}</p>
                <p className="text-sm text-purple-700 mt-1">Devis en attente</p>
              </div>
              <div className="p-4 rounded-lg bg-orange-50 border border-orange-200 text-center">
                <p className="text-3xl font-bold text-orange-600">{stats?.sav?.reparation_en_cours || 0}</p>
                <p className="text-sm text-orange-700 mt-1">En réparation</p>
              </div>
              <div className="p-4 rounded-lg bg-green-50 border border-green-200 text-center">
                <p className="text-3xl font-bold text-green-600">{stats?.sav?.pret_a_restituer || 0}</p>
                <p className="text-sm text-green-700 mt-1">Prêts à restituer</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts & Charts Section */}
      <div className="grid gap-4 md:gap-6 grid-cols-1 lg:grid-cols-2">
        {/* Alerts */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Alertes
            </CardTitle>
            <CardDescription>Points nécessitant votre attention</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {(stats?.alertes?.stock_pieces_faible > 0 || stats?.alertes?.stock_produits_faible > 0) ? (
              <>
                {stats?.alertes?.stock_pieces_faible > 0 && (
                  <AlertCard
                    title="Pièces SAV en stock faible"
                    count={stats.alertes.stock_pieces_faible}
                    type="warning"
                    icon={Package}
                  />
                )}
                {stats?.alertes?.stock_produits_faible > 0 && (
                  <AlertCard
                    title="Produits boutique en stock faible"
                    count={stats.alertes.stock_produits_faible}
                    type="warning"
                    icon={ShoppingCart}
                  />
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <CheckCircle className="h-12 w-12 text-green-500 mb-3" />
                <p className="font-medium">Tout va bien !</p>
                <p className="text-sm text-muted-foreground">Aucune alerte pour le moment</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Brands & Categories */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Laptop className="h-5 w-5 text-primary" />
              Répartition des appareils
            </CardTitle>
            <CardDescription>Par marque et catégorie</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium mb-3">Top marques</p>
                <div className="flex flex-wrap gap-2">
                  {stats?.top_marques?.length > 0 ? (
                    stats.top_marques.map((brand, i) => (
                      <Badge key={i} variant="secondary" className="px-3 py-1">
                        {brand._id || "Autre"}: {brand.count}
                      </Badge>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">Aucune donnée</p>
                  )}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium mb-3">Par catégorie</p>
                <div className="space-y-2">
                  {stats?.repartition_appareils?.slice(0, 5).map((cat, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm capitalize">{cat._id?.replace(/_/g, " ") || "Autre"}</span>
                      <div className="flex items-center gap-2">
                        <Progress value={(cat.count / (stats?.sav?.total || 1)) * 100} className="w-24 h-2" />
                        <span className="text-sm font-mono w-8">{cat.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Actions rapides</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/sav">
              <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2" data-testid="quick-new-sav">
                <Wrench className="h-6 w-6" />
                <span>Nouveau dossier SAV</span>
              </Button>
            </Link>
            <Link to="/clients">
              <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2" data-testid="quick-new-client">
                <Users className="h-6 w-6" />
                <span>Nouveau client</span>
              </Button>
            </Link>
            <Link to="/pos">
              <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2" data-testid="quick-pos">
                <ShoppingCart className="h-6 w-6" />
                <span>Vente comptoir</span>
              </Button>
            </Link>
            <Link to="/quotes">
              <Button variant="outline" className="w-full h-auto py-4 flex flex-col gap-2" data-testid="quick-quotes">
                <FileText className="h-6 w-6" />
                <span>Voir les devis</span>
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
